import React, {useEffect, useRef, useState} from 'react';
import {NavLink, useNavigate, useSearchParams} from "react-router-dom";
import {CheckCircle2, AlertCircle, KeyRound} from "lucide-react";
import {applyActionCode, checkActionCode, confirmPasswordReset, verifyPasswordResetCode} from "firebase/auth";
import Input from "../../ui/Input/Input";
import Button from "../../ui/Button/Button";
import {auth} from "../../../services/firebase_services";
import {checkEmailVerified} from "../../../services/verification";
import {useProfileStore} from "../../../store/profile/pofile.store";
import '../AuthForm/AuthForm.scss';

// Handles the links in Firebase emails (set as the "action URL" in Firebase → Authentication → Templates).
// Firebase adds ?mode=resetPassword|verifyEmail|recoverEmail&oobCode=... to the link.

type Status = 'loading' | 'form' | 'success' | 'error';

const AuthAction = () => {
    const [params] = useSearchParams();
    const mode = params.get('mode');
    const oobCode = params.get('oobCode') || '';
    const navigate = useNavigate();
    const {user, updateUser}: any = useProfileStore();

    const [status, setStatus] = useState<Status>('loading');
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    // Action codes are single-use, so never apply one twice (e.g. on a re-render)
    const handled = useRef(false);

    useEffect(() => {
        if (handled.current) return;
        handled.current = true;

        if (!oobCode) {
            setStatus('error');
            return;
        }
        if (mode === 'resetPassword') {
            verifyPasswordResetCode(auth, oobCode)
                .then((accountEmail) => {
                    setEmail(accountEmail);
                    setStatus('form');
                })
                .catch(() => setStatus('error'));
        } else if (mode === 'verifyEmail') {
            applyActionCode(auth, oobCode)
                .then(async () => {
                    setStatus('success');
                    // If they're signed in here, refresh the profile so the app unlocks right away
                    if (auth.currentUser) await checkEmailVerified(updateUser).catch(() => undefined);
                })
                .catch(() => setStatus('error'));
        } else if (mode === 'recoverEmail') {
            checkActionCode(auth, oobCode)
                .then(async (info) => {
                    setEmail(info.data.email || '');
                    await applyActionCode(auth, oobCode);
                    setStatus('success');
                })
                .catch(() => setStatus('error'));
        } else {
            setStatus('error');
        }
    }, [mode, oobCode, updateUser]);

    const savePassword = async (e: any) => {
        e.preventDefault();
        setError('');
        const password = e.target[0].value;
        if (password !== e.target[1].value) {
            setError("Passwords don't match.");
            return;
        }
        setSaving(true);
        try {
            await confirmPasswordReset(auth, oobCode, password);
            setStatus('success');
        } catch (err: any) {
            if (err?.code === 'auth/weak-password') {
                setError('Password should be at least 6 characters.');
            } else {
                setStatus('error');
            }
        } finally {
            setSaving(false);
        }
    };

    if (status === 'loading') {
        return <div className="form form--center"><span className="form__spinner"/></div>;
    }

    if (status === 'form') {
        return (
            <form onSubmit={savePassword} className="form" action="">
                <div className="form__header">
                    <span className="icon-tile icon-tile--gradient form__icon"><KeyRound size={22}/></span>
                    <h2>Set a new password</h2>
                    <p>For <b>{email}</b></p>
                </div>
                <Input autoComplete={'new-password'} name={'password'} title={'New password (6+ characters)'}
                       type={'password'} required minLength={6}/>
                <Input autoComplete={'new-password'} name={'password'} title={'Confirm new password'}
                       type={'password'} required/>
                {error && <div className="form__error" role="alert"><AlertCircle size={16}/><span>{error}</span></div>}
                <Button variant={'primary'} disabled={saving} title={saving ? 'Saving...' : 'Save password'}/>
            </form>
        );
    }

    if (status === 'success') {
        const content = {
            resetPassword: {
                title: 'Password updated',
                text: 'You can now sign in with your new password.',
                button: 'Sign in',
                to: '/auth',
            },
            verifyEmail: {
                title: 'Email verified',
                text: 'Thank you! You can now create AI chats.',
                button: user ? 'Create a chat' : 'Sign in',
                to: user ? '/create-new-chat' : '/auth',
            },
            recoverEmail: {
                title: 'Email restored',
                text: `Your account email was changed back to ${email}. If you didn't request the change, reset your password to keep your account safe.`,
                button: 'Reset password',
                to: '/auth/reset',
            },
        }[mode as 'resetPassword' | 'verifyEmail' | 'recoverEmail'];

        return (
            <div className="form">
                <div className="form__header">
                    <span className="icon-tile form__icon form__icon--success"><CheckCircle2 size={24}/></span>
                    <h2>{content.title}</h2>
                    <p>{content.text}</p>
                </div>
                <Button type={'button'} variant={'primary'} title={content.button} onClick={() => navigate(content.to)}/>
            </div>
        );
    }

    return (
        <div className="form">
            <div className="form__header">
                <span className="icon-tile form__icon form__icon--error"><AlertCircle size={24}/></span>
                <h2>This link doesn't work anymore</h2>
                <p>
                    It has expired or was already used. Links from Soulify emails work once and only for a limited
                    time.
                </p>
            </div>
            {
                mode === 'resetPassword'
                    ? <Button type={'button'} variant={'primary'} title={'Send a new reset link'}
                              onClick={() => navigate('/auth/reset')}/>
                    : <Button type={'button'} variant={'primary'} title={'Go to Soulify'}
                              onClick={() => navigate(user ? '/' : '/auth')}/>
            }
            <span className="form__switch">
                Need help? <NavLink to={'/auth'}>Back to sign in</NavLink>
            </span>
        </div>
    );
};

export default AuthAction;
